// WP6 Task 9 — the in-app help surface (spec §10): "?" opens a slide-over
// with the 3-5 store entries for the current screen + search + write-to-us.
// Content comes ONLY from @pageboss/shared faq-store via lib/help-content —
// this file owns presentation and a11y, never copy.
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SUPPORT_EMAIL, type ScreenId } from '@pageboss/shared';
import { useFocusTrap } from '@/lib/use-focus-trap';
import { Input } from '@/components/ui/input';
import { screenIdForPath } from '@/lib/screen-ids';
import { HELP_ENTRIES, helpForScreen, searchHelp, type HelpEntry } from '@/lib/help-content';

export interface HelpPanelProps {
  open: boolean;
  onClose: () => void;
  /** Current screen; null → generic fallback list. */
  screenId: ScreenId | null;
}

function EntryList({ entries }: { entries: readonly HelpEntry[] }) {
  return (
    <div className="divide-y divide-border-subtle border-y border-border-subtle">
      {entries.map((e) => (
        <details key={e.id} className="group py-3">
          <summary className="cursor-pointer list-none text-sm font-medium text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
            {e.question}
          </summary>
          <p className="pt-2 text-sm text-text-secondary">{e.answer}</p>
        </details>
      ))}
    </div>
  );
}

export function HelpPanel({ open, onClose, screenId }: HelpPanelProps) {
  const [query, setQuery] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  // Focus restore on close: `useFocusTrap` (below) only traps Tab within the
  // dialog while open — it does not remember or restore the pre-open focus
  // target itself. The established pattern for that (components/modal.tsx,
  // panel-shell.tsx's mobile drawer) captures `document.activeElement` on
  // open and refocuses it in the effect's cleanup; mirrored here.
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const headingId = useId();

  // Fix round 1: the open-effect below must fire ONLY on a real open
  // transition, never on an unrelated parent re-render. HelpLauncher (and
  // any other caller) is free to pass a referentially-fresh `onClose` on
  // every render; if that effect depended on `onClose` directly, every such
  // re-render while the panel was open re-ran the "reset query + refocus
  // search" body, silently clobbering a typed query and yanking focus back
  // mid-keystroke (confirmed live via panel-shell.tsx's Task 10 mount,
  // which re-renders on in-flight setSites/setDomainActive/setUnreadInbox
  // fetches). A ref holds the latest onClose for the ESC handler + cleanup
  // instead, so the effect's dependency array can safely be just `[open]`.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Keep Tab focus inside the panel while it's open — the same primitive
  // every other dialog/drawer in the dashboard uses.
  useFocusTrap(panelRef, open);

  useEffect(() => {
    if (!open) return;

    // Capture BEFORE moving focus into the panel below, so this is the
    // launcher (or whatever else had focus) — never the search input itself.
    restoreFocusRef.current = (document.activeElement as HTMLElement | null) ?? null;
    setQuery('');
    searchRef.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      restoreFocusRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `onClose` is
    // intentionally excluded; see onCloseRef above (fix round 1).
  }, [open]);

  if (!open) return null;

  const searching = query.trim().length > 0;
  const screenResults = searching ? [] : helpForScreen(screenId);
  const results = searching ? searchHelp(query) : screenResults;
  const showingFallback = !searching && screenResults.length === 0;
  const fallback = showingFallback ? HELP_ENTRIES.slice(0, 5) : null;
  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`help: ${screenId ?? 'general'}`)}`;

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="close help"
        className="absolute inset-0 bg-text-primary/40"
        onClick={onClose}
        tabIndex={-1}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className="absolute inset-y-0 right-0 flex w-full max-w-sm flex-col gap-4 overflow-y-auto border-l border-border bg-surface-card p-5 shadow-lg"
      >
        <div className="flex items-center justify-between">
          <h2 id={headingId} className="text-base font-semibold text-text-primary">
            help
          </h2>
          <button
            type="button"
            aria-label="close"
            onClick={onClose}
            className="rounded p-1 text-text-secondary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            ×
          </button>
        </div>
        <Input
          ref={searchRef}
          type="search"
          role="searchbox"
          aria-label="search help"
          placeholder="search the answers…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {searching ? (
          results.length > 0 ? (
            <EntryList entries={results} />
          ) : (
            <p className="text-sm text-text-secondary">
              no answers match — write to us below and a real person answers.
            </p>
          )
        ) : (
          <>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
              {showingFallback ? 'common questions' : 'for this screen'}
            </h3>
            <EntryList entries={showingFallback ? fallback! : results} />
          </>
        )}
        <a
          href={mailto}
          className="mt-auto inline-block text-sm font-medium text-brand-700 underline underline-offset-4"
        >
          still stuck? write to us →
        </a>
      </div>
    </div>
  );
}

export interface HelpLauncherProps {
  /** Optional client-side panel-event emitter (WP1's). Fired as
   *  emit('panel.help_opened', { screen }) on open. NEVER the funnel pipe —
   *  the dashboard has no client analytics wired today (campaign
   *  constraint A3); this prop stays unwired at every real mount and exists
   *  only so a future analytics layer has a slot to plug into. No emitter →
   *  no-op. */
  emit?: (name: string, props?: Record<string, unknown>) => void;
}

export function HelpLauncher({ emit }: HelpLauncherProps) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const screenId = screenIdForPath(location.pathname);

  // Stabilized so HelpPanel's own onCloseRef fix isn't the only line of
  // defense — a fresh arrow here would still be harmless post-fix, but
  // there's no reason to churn it every render either.
  const handleClose = useCallback(() => setOpen(false), []);

  return (
    <>
      <button
        type="button"
        aria-label="help"
        onClick={() => {
          setOpen(true);
          emit?.('panel.help_opened', { screen: screenId ?? 'unknown' });
        }}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-sm font-semibold text-text-secondary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      >
        ?
      </button>
      <HelpPanel open={open} onClose={handleClose} screenId={screenId} />
    </>
  );
}
